// Small shared building blocks: data loading, actions with feedback, and the visual pieces every page uses.
import { createContext, type ReactNode, useCallback, useContext, useEffect, useRef, useState } from "react";
import { ApiError } from "../lib/api";
import { interestLabel, stageLabel, taskByVoxowa, taskSourceLabel, taskSourceTitle } from "../lib/format";
import type { Interest, Stage } from "../lib/types";

export function errorMessage(e: unknown): string {
  if (e instanceof ApiError) return e.message;
  if (e instanceof Error) return e.message;
  return "Something went wrong. Please try again.";
}

/** Loads data when the dependencies change; `reload` fetches again without clearing what is shown. With `keep`, a
 * change of dependencies keeps showing the last data until the new data arrives (a filter changing, D-144). */
export function useLoad<T>(load: () => Promise<T>, deps: unknown[], keep = false) {
  const [state, setState] = useState<
    { data: T | null; error: string | null; loading: boolean }
  >({
    data: null,
    error: null,
    loading: true,
  });
  const seq = useRef(0);
  const run = useCallback(async (clear: boolean) => {
    const n = ++seq.current;
    setState((s) => ({
      data: clear ? null : s.data,
      error: null,
      loading: true,
    }));
    try {
      const data = await load();
      if (n === seq.current) setState({ data, error: null, loading: false });
    } catch (e) {
      if (n === seq.current) {
        setState((s) => ({
          data: clear ? null : s.data,
          error: errorMessage(e),
          loading: false,
        }));
      }
    }
  }, deps);
  useEffect(() => {
    void run(!keep);
  }, [run]);
  return { ...state, reload: () => run(false) };
}

// ------------------------------------------------------------------ toasts

interface Toast {
  id: number;
  text: string;
  error?: boolean;
}

const ToastContext = createContext<(text: string, error?: boolean) => void>(
  () => {},
);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const show = useCallback((text: string, error = false) => {
    const id = Date.now() + Math.random();
    setToasts((t) => [...t, { id, text, error }]);
    setTimeout(
      () => setToasts((t) => t.filter((x) => x.id !== id)),
      error ? 6000 : 3000,
    );
  }, []);
  return (
    <ToastContext.Provider value={show}>
      {children}
      <div className="toasts" role="status" aria-live="polite">
        {toasts.map((t) => (
          <div key={t.id} className={`toast${t.error ? " error" : ""}`}>
            {t.text}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  return useContext(ToastContext);
}

/** Runs an action, shows a toast when it works or the error when it does not, and tracks whether it is busy. */
export function useAction() {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const run = useCallback(
    async (action: () => Promise<unknown>, done?: string): Promise<boolean> => {
      setBusy(true);
      try {
        await action();
        if (done) toast(done);
        return true;
      } catch (e) {
        toast(errorMessage(e), true);
        return false;
      } finally {
        setBusy(false);
      }
    },
    [toast],
  );
  return { busy, run };
}

// ---------------------------------------------------------------- visuals

export function Page(
  props: {
    title: ReactNode;
    sub?: ReactNode;
    actions?: ReactNode;
    children: ReactNode;
  },
) {
  return (
    <>
      <div className="page-head">
        <div>
          <h1>{props.title}</h1>
          {props.sub && <div className="sub">{props.sub}</div>}
        </div>
        {props.actions && <div className="row">{props.actions}</div>}
      </div>
      {props.children}
    </>
  );
}

export function Card(
  props: {
    title?: ReactNode;
    actions?: ReactNode;
    flush?: boolean;
    children: ReactNode;
    className?: string;
  },
) {
  return (
    <section
      className={`card${props.flush ? " flush" : ""}${props.className ? ` ${props.className}` : ""}`}
    >
      {(props.title || props.actions) && (
        <div className="card-head">
          {typeof props.title === "string" ? <h2>{props.title}</h2> : props.title}
          {props.actions && <div className="row">{props.actions}</div>}
        </div>
      )}
      {props.children}
    </section>
  );
}

export function Stat(
  props: {
    label: string;
    value: ReactNode;
    hint?: ReactNode;
    alert?: boolean;
    href?: string;
  },
) {
  const body = (
    <>
      <div className="label">{props.label}</div>
      <div className="value">{props.value}</div>
      {props.hint && <div className="hint">{props.hint}</div>}
    </>
  );
  const cls = `card stat${props.alert ? " alert" : ""}`;
  return props.href ? <a className={cls} href={props.href}>{body}</a> : <div className={cls}>{body}</div>;
}

export function Loading({ text = "Loading…" }: { text?: string }) {
  return (
    <div className="loading">
      <span className="spinner" aria-hidden /> {text}
    </div>
  );
}

export function ErrorNote({ error }: { error: string | null | undefined }) {
  return error ? <div className="error-note" role="alert">{error}</div> : null;
}

export function Empty({ children }: { children: ReactNode }) {
  return <div className="empty">{children}</div>;
}

export function InterestBadge({ interest }: { interest: Interest | null }) {
  if (!interest) return <span className="faint">–</span>;
  const tone = interest === "none" ? "" : interest;
  return <span className={`badge ${tone}`}>{interestLabel[interest]}</span>;
}

/** Voxowa's mark, as on the browser tab. */
export function VoxowaMark() {
  return (
    <svg className="voxowa-mark" viewBox="0 0 64 64" aria-hidden="true">
      <rect width="64" height="64" rx="16" fill="#008049" />
      <path
        d="M15 35L27 47L45 23"
        fill="none"
        stroke="white"
        strokeWidth="8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="50.5" cy="15.5" r="5.5" fill="white" />
    </svg>
  );
}

/** Who made a follow-up: Voxowa's mark before what Voxowa made (its AI, its rules, an import), so a wrong follow-up
 * shows whether Voxowa's logic or a person set it (WEB-08, D-109). */
export function TaskSource({ source, rule }: { source: string; rule: string | null }) {
  const byVoxowa = taskByVoxowa(source);
  return (
    <span className={byVoxowa ? "task-source by-voxowa" : "task-source"} title={taskSourceTitle(source, rule)}>
      {byVoxowa && <VoxowaMark />}
      {taskSourceLabel(source, rule)}
    </span>
  );
}

export function StageBadge({ stage }: { stage: Stage }) {
  const tone = stage === "won" ? "ok" : stage === "lost" || stage === "not_a_lead" ? "" : "brand";
  return <span className={`badge ${tone}`}>{stageLabel[stage]}</span>;
}

export function Field(
  props: { label: string; hint?: ReactNode; children: ReactNode },
) {
  return (
    <label className="field">
      <span>{props.label}</span>
      {props.children}
      {props.hint && <span className="hint">{props.hint}</span>}
    </label>
  );
}

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), ' +
  'textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** A dialog: the keyboard goes into it when it opens, Tab stays inside it, Esc closes it, and the keyboard returns
 * to what opened it. */
export function Modal(
  props: { title: string; onClose: () => void; children: ReactNode },
) {
  const ref = useRef<HTMLDivElement>(null);
  const onClose = useRef(props.onClose);
  onClose.current = props.onClose;
  useEffect(() => {
    const before = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const dialog = ref.current;
    if (dialog && !dialog.contains(document.activeElement)) {
      (dialog.querySelector<HTMLElement>(FOCUSABLE) ?? dialog).focus();
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose.current();
      if (e.key !== "Tab" || !dialog) return;
      const items = [...dialog.querySelectorAll<HTMLElement>(FOCUSABLE)].filter((el) =>
        el.offsetParent !== null ||
        el.getClientRects().length > 0
      );
      if (items.length === 0) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && (document.activeElement === first || !dialog.contains(document.activeElement))) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (document.activeElement === last || !dialog.contains(document.activeElement))) {
        e.preventDefault();
        first.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      before?.focus();
    };
  }, []);
  return (
    <div
      className="modal-scrim"
      onMouseDown={(e) => e.target === e.currentTarget && props.onClose()}
    >
      <div
        ref={ref}
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-label={props.title}
        tabIndex={-1}
      >
        <h2>{props.title}</h2>
        {props.children}
      </div>
    </div>
  );
}

/** A button that asks "Sure?" inline before doing something that cannot be undone. */
export function ConfirmButton(
  props: {
    label: string;
    confirm: string;
    onConfirm: () => void;
    disabled?: boolean;
  },
) {
  const [asking, setAsking] = useState(false);
  if (!asking) {
    return (
      <button
        className="small danger"
        disabled={props.disabled}
        onClick={() => setAsking(true)}
      >
        {props.label}
      </button>
    );
  }
  return (
    <span className="row">
      <span className="faint">{props.confirm}</span>
      <button
        className="small danger solid"
        onClick={() => {
          setAsking(false);
          props.onConfirm();
        }}
      >
        Yes
      </button>
      <button className="small" onClick={() => setAsking(false)}>No</button>
    </span>
  );
}

export function Bars({ data }: { data: { label: string; value: number }[] }) {
  const max = Math.max(1, ...data.map((d) => d.value));
  return (
    <div className="bars" role="img" aria-label="Minutes per day">
      {data.map((d) => (
        <div
          key={d.label}
          title={`${d.label}: ${d.value}`}
          style={{ height: `${(d.value / max) * 100}%` }}
        />
      ))}
    </div>
  );
}
